import { jsPDF } from 'jspdf';
import { DESCRIPTORES_NIVEL, fichaPorCodigo } from '@cessi/content';
import { DIMENSION_LABELS, DIMENSIONES_NIVEL } from '@cessi/shared';
import type { BenchmarkPayload, ScorePayload } from './api';

// Identidad del sitio del framework: tinta/carbón + amarillo solo como fondo.
const BRAND = '#222222';
const INK = '#222222';
const MUTED = '#6E6E6E';
const RULE = '#DDDDDD';
/** Links y cifras destacadas: tinta (el amarillo no es legible como texto sobre blanco). */
const ACCENT = '#222222';
const CHARCOAL_RGB = [60, 60, 60] as const;
const YELLOW_RGB = [244, 241, 119] as const;
const SOFT_RGB = [245, 245, 245] as const;

export type PerfilPdfInput = {
  organizacion: string;
  nombre?: string;
  score: ScorePayload;
  benchmark?: BenchmarkPayload | null;
  fecha?: Date;
};

/** Nombre de archivo seguro para el one-pager. */
export function nombreArchivoPerfil(organizacion: string, fecha = new Date()): string {
  const slug = organizacion
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
    .toLowerCase() || 'organizacion';
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, '0');
  const d = String(fecha.getDate()).padStart(2, '0');
  return `perfil-madurez-ia-${slug}-${y}${m}${d}.pdf`;
}

function numero(valor: number): string {
  return valor.toLocaleString('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function fechaEs(fecha: Date): string {
  return fecha.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Genera y descarga el one-pager PDF del perfil de madurez
 * (misma información que la pantalla de resultados).
 */
export function descargarPerfilPdf(input: PerfilPdfInput): void {
  const doc = construirPerfilPdf(input);
  doc.save(nombreArchivoPerfil(input.organizacion, input.fecha ?? new Date()));
}

/** Construye el documento (exportado para tests). */
export function construirPerfilPdf(input: PerfilPdfInput): jsPDF {
  const { score, organizacion, nombre, benchmark } = input;
  const fecha = input.fecha ?? new Date();
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 18;
  const contentW = pageW - marginX * 2;
  let y = 0;

  // — Cabecera marca —
  doc.setFillColor(...CHARCOAL_RGB);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setFillColor(...YELLOW_RGB);
  doc.rect(0, 28, pageW, 1.6, 'F');
  doc.setTextColor(...YELLOW_RGB);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Espacio de Innovación con IA · CESSI', marginX, 12);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Perfil de madurez — Autoevaluación de Adopción e Innovación con IA', marginX, 20);
  y = 38;

  // — Organización —
  doc.setTextColor(INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  const orgLines = doc.splitTextToSize(organizacion || 'Organización', contentW);
  doc.text(orgLines, marginX, y);
  y += orgLines.length * 7 + 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(MUTED);
  const meta = [
    nombre ? `Respondente: ${nombre}` : null,
    fechaEs(fecha),
  ]
    .filter(Boolean)
    .join(' · ');
  doc.text(meta, marginX, y);
  y += 8;

  // — Nivel general —
  doc.setDrawColor(RULE);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, marginX + contentW, y);
  y += 8;

  const descriptor = DESCRIPTORES_NIVEL[score.nivel_general].replace(/^N\d\s*·\s*/, '');
  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(`Nivel general N${score.nivel_general}`, marginX, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(INK);
  const descLines = doc.splitTextToSize(descriptor, contentW);
  doc.text(descLines, marginX, y);
  y += descLines.length * 5 + 4;

  if (score.arquetipo || score.segmento) {
    doc.setTextColor(MUTED);
    doc.setFontSize(9);
    doc.text(
      [score.arquetipo, score.segmento].filter(Boolean).join(' · '),
      marginX,
      y,
    );
    y += 6;
  }

  // — Índice de innovación —
  doc.setFillColor(...SOFT_RGB);
  doc.roundedRect(marginX, y, contentW, 16, 2, 2, 'F');
  doc.setFillColor(...YELLOW_RGB);
  doc.rect(marginX, y, 1.2, 16, 'F');
  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(DIMENSION_LABELS.IN, marginX + 4, y + 6.5);
  doc.setFontSize(16);
  doc.setTextColor(ACCENT);
  doc.text(numero(score.indice_IN), marginX + contentW - 4, y + 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text('escala 0–4', marginX + 4, y + 12.5);
  y += 22;

  // — Dimensiones —
  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Niveles por dimensión', marginX, y);
  y += 6;

  const hasBenchmark =
    Boolean(benchmark?.enabled && benchmark.promedioPorDimension);

  // Tabla header
  doc.setFillColor(...CHARCOAL_RGB);
  doc.rect(marginX, y, contentW, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Dimensión', marginX + 2, y + 4.8);
  doc.text('Su nivel', marginX + contentW * 0.55, y + 4.8);
  doc.text('Promedio', marginX + contentW * 0.7, y + 4.8);
  if (hasBenchmark) {
    doc.text('Industria', marginX + contentW * 0.85, y + 4.8);
  }
  y += 7;

  doc.setFont('helvetica', 'normal');
  for (const code of DIMENSIONES_NIVEL) {
    const nivel = score.niveles[code];
    const prom = score.promedios?.[code];
    const media = hasBenchmark
      ? benchmark!.promedioPorDimension![code]
      : null;
    const foco = score.dimension_foco === code;

    if (foco) {
      doc.setFillColor(...YELLOW_RGB);
      doc.rect(marginX, y, contentW, 7.5, 'F');
    }

    doc.setTextColor(INK);
    doc.setFontSize(9);
    doc.setFont('helvetica', foco ? 'bold' : 'normal');
    const label = DIMENSION_LABELS[code] + (foco ? '  ← foco' : '');
    doc.text(label, marginX + 2, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.text(`N${nivel}`, marginX + contentW * 0.55, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.text(prom != null ? numero(prom) : '—', marginX + contentW * 0.7, y + 5);
    if (hasBenchmark && media != null) {
      doc.setTextColor(MUTED);
      doc.text(numero(media), marginX + contentW * 0.85, y + 5);
    }
    y += 7.5;
  }
  y += 4;

  // — Tres capacidades —
  doc.setDrawColor(RULE);
  doc.line(marginX, y, marginX + contentW, y);
  y += 8;

  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Tres capacidades recomendadas', marginX, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text(
    'Fichas del Framework de Adopción de IA para priorizar en el próximo trimestre.',
    marginX,
    y,
  );
  y += 7;

  const fichas = score.fichas ?? [];
  const nivelesFicha = score.fichas_niveles ?? [];
  const links =
    score.fichas_links ??
    fichas.map((codigo, i) => {
      const f = fichaPorCodigo(codigo);
      return {
        codigo,
        nombre: f?.nombre ?? codigo,
        url: '',
        nivel: (nivelesFicha[i] ?? 1) as 1 | 2 | 3 | 4,
      };
    });

  if (links.length === 0) {
    doc.setTextColor(MUTED);
    doc.setFontSize(9);
    doc.text('Sin fichas disponibles en este resultado.', marginX, y);
    y += 6;
  } else {
    links.forEach((l, idx) => {
      if (idx % 2 === 0) doc.setFillColor(...SOFT_RGB);
      else doc.setFillColor(255, 255, 255);
      doc.roundedRect(marginX, y, contentW, 14, 1.5, 1.5, 'F');
      doc.setTextColor(BRAND);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${idx + 1}. ${l.codigo} · ${l.nombre}`, marginX + 3, y + 5.5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(INK);
      doc.text(`Nivel actual N${l.nivel}`, marginX + 3, y + 10.5);
      if (l.url) {
        doc.setTextColor(ACCENT);
        const urlShort =
          l.url.length > 72 ? `${l.url.slice(0, 69)}…` : l.url;
        doc.textWithLink(urlShort, marginX + 38, y + 10.5, { url: l.url });
      }
      y += 16;
    });
  }

  if (score.recomendacion_url) {
    y += 2;
    doc.setTextColor(ACCENT);
    doc.setFontSize(8);
    doc.textWithLink(
      'Ver las tres fichas juntas en el sitio del framework →',
      marginX,
      y,
      { url: score.recomendacion_url },
    );
    y += 8;
  }

  // — Pie —
  const footerY = 285;
  doc.setDrawColor(RULE);
  doc.line(marginX, footerY - 6, marginX + contentW, footerY - 6);
  doc.setTextColor(MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const pie = doc.splitTextToSize(
    'Espacio de Innovación con IA · CESSI. Resultados individuales no se comparten con terceros. Datos tratados conforme a la Ley 25.326. Este perfil es orientativo y refleja la autoevaluación declarada.',
    contentW,
  );
  doc.text(pie, marginX, footerY);

  return doc;
}
